import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { updateAiAssistantCommentReplyMode } from '../services/companyService';
import type { AiAssistantSettings, AssistantCommentReplyMode } from '../types/Company';
import {
  ASSISTANT_COMMENT_REPLY_CHANNELS,
  ASSISTANT_COMMENT_REPLY_MODES,
  resolveAssistantCommentReplyModeForChannel,
  type AssistantCommentReplyChannel,
} from '../utils/assistantCommentReplyChannels';
import styles from './AssistantChannelMutePanels.module.css';

const CHANNEL_CARD_CLASS: Record<AssistantCommentReplyChannel, string> = {
  instagram: styles.cardInstagram,
  messenger: styles.cardMessenger,
  tiktok: styles.cardTiktok,
};

interface AssistantCommentReplyModePanelProps {
  settings: Pick<AiAssistantSettings, 'aiAssistantCommentReplyModeByChannel'>;
  onSettingsChange: (settings: Pick<AiAssistantSettings, 'aiAssistantCommentReplyModeByChannel'>) => void;
  onNotice: (message: string) => void;
  onError: (message: string) => void;
}

export const AssistantCommentReplyModePanel: React.FC<AssistantCommentReplyModePanelProps> = ({
  settings,
  onSettingsChange,
  onNotice,
  onError,
}) => {
  const { t } = useTranslation('companies');
  const [activeChannel, setActiveChannel] = React.useState<AssistantCommentReplyChannel | null>(null);

  const runModeUpdate = React.useCallback(async (
    channel: AssistantCommentReplyChannel,
    mode: AssistantCommentReplyMode,
  ) => {
    const previousSettings = settings.aiAssistantCommentReplyModeByChannel;
    setActiveChannel(channel);
    onError('');

    onSettingsChange({
      aiAssistantCommentReplyModeByChannel: {
        ...previousSettings,
        [channel]: mode,
      },
    });

    try {
      const result = await updateAiAssistantCommentReplyMode({ channel, mode });
      onSettingsChange({
        aiAssistantCommentReplyModeByChannel: {
          ...previousSettings,
          [channel]: result.mode,
        },
      });

      const channelLabel = t(ASSISTANT_COMMENT_REPLY_CHANNELS.find(item => item.key === channel)!.labelKey);
      onNotice(t('assistantCommentReplyModeUpdated', { channel: channelLabel }));
    } catch (error) {
      onSettingsChange({ aiAssistantCommentReplyModeByChannel: previousSettings });
      onError(error instanceof Error ? error.message : t('assistantCommentReplyModeUpdateFailed'));
    } finally {
      setActiveChannel(null);
    }
  }, [onError, onNotice, onSettingsChange, settings.aiAssistantCommentReplyModeByChannel, t]);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h4 className={styles.sectionTitle}>{t('assistantCommentReplyModeHeading')}</h4>
        <p className={styles.sectionDescription}>{t('assistantCommentReplyModeDescription')}</p>
        <p className={styles.crossLink}>
          {t('assistantCommentAutomationsCrossLink')}{' '}
          <Link to="/admin/automations?tab=comments">{t('assistantCommentAutomationsCrossLinkCta')}</Link>
        </p>
      </div>

      <div className={styles.grid}>
        {ASSISTANT_COMMENT_REPLY_CHANNELS.map(channel => {
          const channelLabel = t(channel.labelKey);
          const selectedMode = resolveAssistantCommentReplyModeForChannel(
            settings.aiAssistantCommentReplyModeByChannel,
            channel.key,
          );
          const busy = activeChannel === channel.key;

          return (
            <div
              key={channel.key}
              className={`${styles.card} ${CHANNEL_CARD_CLASS[channel.key]} ${!channel.available ? styles.cardDisabled : ''}`}
            >
              <div className={styles.cardCopy}>
                <span className={styles.channelName}>{channelLabel}</span>
                <span className={styles.channelHint}>
                  {channel.available
                    ? t(`assistantCommentReplyMode_${selectedMode}Hint`)
                    : t('assistantCommentReplyModeComingSoon')}
                </span>
              </div>

              <div className={styles.modeToggleGroup} aria-disabled={!channel.available || busy}>
                {ASSISTANT_COMMENT_REPLY_MODES.map(mode => (
                  <button
                    key={mode}
                    type="button"
                    className={`${styles.modeToggleButton} ${selectedMode === mode ? styles.modeToggleButtonActive : ''}`}
                    disabled={!channel.available || busy || activeChannel !== null}
                    onClick={() => void runModeUpdate(channel.key, mode)}
                  >
                    {t(`instagramDefaultCommentReplyMode_${mode}`)}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AssistantCommentReplyModePanel;
